"use strict";
/**
 * Order Execution
 *
 * ONE flow for every venue. This used to be three near-identical ~80-line
 * functions whose only real difference was how a fill count is read out of a
 * venue's response; that read now lives in each adapter and arrives here as a
 * `VenueOrderResult` (see venues/types.ts).
 *
 * Hot path — `ai/skills/hot-path-change.md`. The shape to preserve:
 * ONE synchronous build, ONE await, then state. No extra awaits, no fetches,
 * no re-sorting a book (the build reads `appState.cachedBidAsk`, which the
 * poll tick pre-computed).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.extractPmUsFilledQty = void 0;
exports.placeNewOrder = placeNewOrder;
const state_1 = require("../state");
const api_1 = require("../api");
const ui_1 = require("../ui");
const registry_1 = require("../venues/registry");
const price_sample_arm_1 = require("./price-sample-arm");
/**
 * Compatibility re-export: PM-US's fill rule lives beside the venue that owns
 * it, but is imported from here, where it has always been.
 */
var pm_us_adapter_1 = require("../venues/pm-us-adapter");
Object.defineProperty(exports, "extractPmUsFilledQty", { enumerable: true, get: function () { return pm_us_adapter_1.extractPmUsFilledQty; } });
/**
 * Determine if the current market is a Polymarket market
 */
function getCurrentDataSource() {
    const state = (0, state_1.getState)();
    if (!state.dataSource) {
        console.warn("[orders] dataSource missing in state; defaulting to kalshi");
    }
    return state.dataSource ?? "kalshi";
}
/**
 * Place a new order.
 *
 * @param marketId - Market ticker
 * @param side - yes or no
 * @param action - buy or sell
 * @param venueOverride - Optional venue to route to, overriding the global
 *                        dataSource. Used by additional-market orders whose
 *                        venue may differ from the primary market's.
 * @returns Order object
 */
async function placeNewOrder(marketId, side, action = "buy", venueOverride, options) {
    const venue = venueOverride ?? getCurrentDataSource();
    const timings = {
        start: performance.now(),
        requestCreation: 0,
        apiCall: 0,
        total: 0,
    };
    // Resolved INSIDE the try: fan-out venues come from localStorage records
    // that are never validated on load, so an unrecognised one is a real
    // possibility. getVenue throws on it — which is correct, and far better
    // than the previous `else` that silently placed the order on Kalshi — but
    // it has to surface as an order failure like any other, not as an
    // unhandled throw past the glow and the error message.
    let adapter = null;
    try {
        adapter = (0, registry_1.getVenue)(venue);
        const ctx = {
            marketId,
            side,
            action,
            options: options ?? {},
        };
        // Synchronous by contract — see the file header.
        const request = adapter.buildOrderRequest(ctx);
        timings.requestCreation = performance.now() - timings.start;
        (0, state_1.updateState)({ isPlacingOrder: true, errorMessage: null });
        const apiStart = performance.now();
        const result = await adapter.placeOrder(request, ctx);
        timings.apiCall = performance.now() - apiStart;
        timings.total = performance.now() - timings.start;
        console.log(`[ORDER TIMING] Total: ${timings.total.toFixed(2)}ms`, {
            venue,
            requestCreation: `${timings.requestCreation.toFixed(2)}ms`,
            apiCall: `${timings.apiCall.toFixed(2)}ms`,
        });
        console.log(`[${venue.toUpperCase()} ORDER] Response:`, result.raw);
        (0, ui_1.showBorderGlow)(result.succeeded ? "success" : "error");
        // null means the venue cannot report a fill at placement — no badge,
        // rather than one asserting a fill it never confirmed.
        if (result.filledQty !== null) {
            (0, ui_1.showFillCountBadge)(result.filledQty, side);
        }
        // Arm the MARK button when this click bought something. Post-ack, so
        // it costs the order path nothing; only venues in the order log.
        const openedQty = result.openedQty ?? result.filledQty;
        if ((0, price_sample_arm_1.toSampleVenue)(venue) && openedQty !== null && openedQty > 0) {
            (0, price_sample_arm_1.reportClickFill)(options?.clickId, action, openedQty);
        }
        (0, state_1.updateState)({
            isPlacingOrder: false,
            lastOrderStatus: result.statusForState,
            ...(result.warning ? { errorMessage: result.warning } : {}),
        });
        return result.order;
    }
    catch (error) {
        timings.total = performance.now() - timings.start;
        console.log(`[ORDER TIMING] Failed: ${timings.total.toFixed(2)}ms`);
        console.error(`[${venue.toUpperCase()} ORDER] Failed to place order:`, error);
        const label = adapter?.orderErrorLabel ?? "Order";
        const message = error instanceof api_1.ApiRequestError || error instanceof Error
            ? `${label} failed: ${error.message}`
            : `Failed to place ${label.toLowerCase()}`;
        (0, state_1.updateState)({ isPlacingOrder: false, errorMessage: message });
        (0, ui_1.showBorderGlow)("error");
        throw error;
    }
}
